import {
  type Type,
  Plugins,
  Types,
  Expressions,
  VariableMap,
  EnvTypes,
  EvalEnv,
  Variable,
  withSystem,
} from '@packages/systems/wfdl';
import {ELiteral, type Expression} from '@packages/systems/wfdl-ast';
import {identifier} from '@packages/systems/wfdl-Qualified';
import {isDynamoVariable} from '@packages/systems/dynamo/wfdl';
import {getCurrentOrDefaultExpression} from '@packages/systems/core/component-conversions';
import {
  localesListItemVariable,
  localesListActiveItemVariable,
  pageVariable,
} from '@packages/shared/constants/Render';
import {toSelectPath} from '@packages/utilities/wfdl-helpers';
import {
  LINK_TYPE,
  getModalTargetReferenceForRead,
  getOverlayTargetReferenceFromLinkLiteral,
  getPopoverTargetReferenceForRead,
  type OverlayTargetReference,
} from '@packages/systems/overlays';
import {propertyTypes} from './types';

const dataVariableName = 'data';
const contextVariable = Variable.unqualified(identifier('context'));

// Mirrors getLocalizationBindingFromSelect without importing @packages/systems/localization,
// which would introduce a circular dependency through this package's importers.
const isLocalizationBindingSelect = (expr: Expression): boolean => {
  const {from, path} = toSelectPath(expr);
  if (path.length !== 3) return false;

  const [localizationRoot, componentId, localizationKey] = path;
  if (
    localizationRoot !== 'localizations' ||
    !componentId ||
    !localizationKey
  ) {
    return false;
  }

  const fromVariable = Expressions.getVariable(from);
  return Boolean(
    fromVariable && Variable.equals(contextVariable)(fromVariable)
  );
};

// Reproduces the locale/page checks from findBindingInfo
// (public/js/designer-flux/utils/BindingSourceUtils.ts). That util lives in the
// Designer app layer, which packages can't import — the dependency only points
// app → packages, and this file is also bundled server-side. So the checks are
// reproduced here against the same shared constants.
const isLocalesListItemVariable = Variable.equals(localesListItemVariable);
const isLocalesListActiveItemVariable = Variable.equals(
  localesListActiveItemVariable
);
const isPageVariable = Variable.equals(pageVariable);

// True when the expression contains any binding: a component-prop (`data.x`),
// CMS (`Dynamo.x`), Localization (`localesListItem`/`localesListActiveItem`),
// or Page (`page.x`) reference.
const containsBinding = (expr: Expression): boolean =>
  Expressions.some((e) => {
    const select = Expressions.getSelect(e);
    if (!select) return false;
    if (Expressions.isDataVariable(select.from)) return true;
    if (isDynamoVariable(select.from)) return true;
    if (isLocalizationBindingSelect(e)) return true;
    const fromVariable = Expressions.getVariable(select.from);
    return Boolean(
      fromVariable &&
      (isLocalesListItemVariable(fromVariable) ||
        isLocalesListActiveItemVariable(fromVariable) ||
        isPageVariable(fromVariable))
    );
  })(expr);

// True when `expr` is a bound Attributes value. It can't be captured as a
// static default, so callers route it elsewhere (Suggested Props transfers it
// to an instance override) instead of evaluating it into a default.
export const isAttributesValueWithBinding = (
  expr: Expression,
  dataType: Type
): boolean => Types.isAttributesType(dataType) && containsBinding(expr);

const readOverlayTargetReferenceAt = (
  expression: Expression
): OverlayTargetReference | null => {
  const popoverReference = getPopoverTargetReferenceForRead(expression);
  if (popoverReference) return {kind: 'popover', ...popoverReference};

  const modalReference = getModalTargetReferenceForRead(expression);
  if (modalReference) return {kind: 'modal', ...modalReference};

  return getOverlayTargetReferenceFromLinkLiteral(expression);
};

const getUntargetedOverlayLink = ({
  kind,
  action,
}: OverlayTargetReference): Expression =>
  ELiteral({
    name: LINK_TYPE,
    value:
      kind === 'modal'
        ? {mode: 'modal', modalAction: action}
        : {mode: 'popover', popoverAction: action},
  });

// Overlay targets only resolve inside the component, so a default keeps the
// mode and action and leaves the target for each instance to set.
const untargetOverlayLinks = Expressions.everywhere((expression) => {
  const reference = readOverlayTargetReferenceAt(expression);
  return reference ? getUntargetedOverlayLink(reference) : expression;
});

export const getNewFieldDefaultValue = ({
  strippedExpr,
  plugins,
  dataType,
  envTypes,
  evaluateAttributes,
}: {
  strippedExpr: Expression;
  plugins: Plugins.PluginsType;
  dataType: Type;
  envTypes: VariableMap.Map<EnvTypes.NormalizedEnvType>;
  // Whether to evaluate a static Attributes value into the default. The binding
  // flow (Expression Editor "Add new field") passes false: it preserves static
  // items via an Append on the element, so seeding the default would duplicate
  // them. Suggested Props passes true to carry the static value in the default.
  // A bound Attributes value is never evaluated regardless — that would drop the
  // binding. Required (no default) so a new caller can't silently inherit the
  // lossy empty-default behavior.
  evaluateAttributes: boolean;
}) => {
  if (
    Types.isAttributesType(dataType) &&
    (!evaluateAttributes || containsBinding(strippedExpr))
  ) {
    return [];
  }

  const unholeyExpression = Expressions.fromHoleyExpression(strippedExpr);
  // If we have holes in our expression, inherit the default
  // value from the base type:
  if (unholeyExpression == null) {
    return Types.getTypeDefaultValue(plugins)(dataType);
  }

  const typeForData = EnvTypes.getType(envTypes, dataVariableName);

  let evalEnv = Plugins.getEvalEnv(plugins);
  // If we have props, make sure to include them in the eval env
  if (typeForData) {
    const propDefaults: {[key: string]: any} = {};
    const propFields = Types.getRecordFields(typeForData);
    if (propFields) {
      Object.keys(propFields).forEach((key) => {
        const propField = propFields[key];
        if (propField) {
          propDefaults[key] = Types.getTypeDefaultValue(plugins)(propField);
        }
      });
    }
    // Add these to the evalEnv
    evalEnv = EvalEnv.insertValue(evalEnv, dataVariableName, propDefaults);
  }

  return Expressions.evaluate(evalEnv, unholeyExpression);
};

export const getNewFieldDefaultExpression = (args: {
  expression: Expression;
  plugins: Plugins.PluginsType;
  dataType: Type;
  envTypes: VariableMap.Map<EnvTypes.NormalizedEnvType>;
  evaluateAttributes: boolean;
}): Expression => {
  const {plugins, dataType} = args;
  const expression = Types.equal(dataType, propertyTypes['Link'].type)
    ? untargetOverlayLinks(args.expression)
    : args.expression;
  const value = getNewFieldDefaultValue({
    strippedExpr: expression,
    plugins,
    dataType,
    envTypes: args.envTypes,
    evaluateAttributes: args.evaluateAttributes,
  });
  return getCurrentOrDefaultExpression(plugins)(
    withSystem({defaultValue: value}, dataType)
  );
};
